import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, bosLoginUrl } from '../api';
import { getDefaultPortalRoute } from '../utils';
import { returnPathAfterSignIn } from '../components/auth/returnPath';
import { Icon } from '../components/ui';
import './Auth.css';

/**
 * Why a "Sign in with BOS" attempt came back here, by the `?sso_error=` code the server
 * sends (accounts/bos_sso.py). Fixed sentences: nothing the identity provider said is ever
 * echoed onto the page.
 */
export const BOS_SIGN_IN_ERRORS: Record<string, string> = {
  not_linked:
    'There is no account here for your BOS sign-in yet, and your company is not linked to a ' +
    'workspace. Ask a workspace administrator to invite you.',
  email_unverified: 'Verify your email address in BOS first, then try again.',
  email_missing: 'Your BOS account has no email address, so it cannot be matched to an account here.',
  inactive: 'This account has been deactivated. Ask a workspace administrator.',
  cancelled: 'Sign in with BOS was cancelled.',
  expired: 'That sign-in has expired. Choose Sign in with BOS again.',
  unavailable:
    'Sign in with BOS is unavailable right now. Use your email and password, or try again shortly.',
  failed: 'Sign in with BOS did not complete. Try again.',
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // 'forgot' turns the panel into a dedicated "request a reset link" view rather than the
  // old inline button that silently required the email to already be typed above it.
  const [mode, setMode] = useState<'signin' | 'forgot'>('signin');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const inviteToken = searchParams.get('invite') || '';
  const invitedEmail = searchParams.get('email') || '';
  const ssoError = searchParams.get('sso_error') || '';
  const registerHref = inviteToken ? `/register?invite=${encodeURIComponent(inviteToken)}` : '/register';
  // Offered only when the server says single sign-on is on AND configured. Off by default,
  // and any failure to ask (an older server, a network blip) leaves it hidden.
  const [bosEnabled, setBosEnabled] = useState(false);

  useEffect(() => {
    let live = true;
    authAPI.bosConfig()
      .then((res) => { if (live) setBosEnabled(res.data?.enabled === true); })
      .catch(() => { if (live) setBosEnabled(false); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (ssoError) {
      setError(BOS_SIGN_IN_ERRORS[ssoError] || BOS_SIGN_IN_ERRORS.failed);
    }
  }, [ssoError]);

  useEffect(() => {
    if (invitedEmail) {
      setForm((prev) => ({ ...prev, email: invitedEmail }));
    }
    if (inviteToken) {
      setError('Sign in with this email to join your invited workspace.');
    }
  }, [inviteToken, invitedEmail]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      // Route by the role the server just returned. This call used to pass no role at
      // all, so every user — Owner included — was routed as a Viewer and landed on
      // /portal/home regardless of what they could actually reach.
      const signedIn = await login(form.email, form.password);

      // Redeem the invitation the link carried, now that we are somebody.
      //
      // This page already read `?invite=` and already told the user signing in would join
      // them to the workspace. Nothing acted on it, so the sentence was false: they signed
      // in, landed on their own workspace, and the invited one was still invisible —
      // `Invited` grants nothing until something activates it.
      //
      // A failure here does not block the sign-in, which genuinely succeeded. It is
      // surfaced on the next page instead of being swallowed, because "you are in" for
      // somebody who is not in is the kind of half-true success this codebase has been
      // bitten by before.
      let inviteError = '';
      if (inviteToken) {
        try {
          await authAPI.acceptInvite(inviteToken);
        } catch (err: any) {
          inviteError =
            err?.response?.data?.error ||
            'Signed in, but the invitation could not be accepted. Ask for a new link.';
        }
      }

      // Back to where they were going, if anywhere: the token page the integration guide
      // links to, or the task an email linked to. Not after redeeming an invitation, where
      // the place to go is the workspace just joined. No role check here: the portal's own
      // route guard already turns away a page the role cannot open.
      //
      // No stored "landing page": it was chosen on the old Setup page, which is gone, and a
      // preference nobody can see or change any more must not decide where people land.
      const returnTo = inviteToken ? null : returnPathAfterSignIn(location.state, location.search);
      navigate(
        returnTo || getDefaultPortalRoute(null, signedIn?.current_role),
        { replace: true, state: inviteError ? { inviteError } : undefined },
      );
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Login failed. Please check your credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e?: React.FormEvent) => {
    e?.preventDefault();
    // This used to prefill a real administrator account and its password — the pair that
    // had also been published in a committed CREDENTIALS.md — which meant the sign-in page
    // shipped a working administrator credential inside its own JavaScript bundle, one
    // click from any visitor. The literal values are deliberately not repeated here:
    // comments ship, ending up in the bundle and in the source map CRA serves beside it.
    //
    // It then said self-service reset was unavailable and to ask a workspace owner. True
    // at the time, and unworkable for a team onboarded in bulk, where the generated
    // passwords are printed once and stored nowhere.
    if (!form.email) {
      setError('Enter your email address first, then choose Forgot password.');
      return;
    }
    setResetting(true);
    setError('');
    try {
      const res = await authAPI.requestPasswordReset(form.email);
      // Deliberately the server's own sentence, which is identical whether or not the
      // address exists. Saying "we could not find that account" here would undo the
      // reason the endpoint refuses to distinguish them.
      setNotice(res.data?.detail || 'If that address has an account, a reset link is on its way.');
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
        'Could not request a reset just now. Try again in a moment.',
      );
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-brand">
          <Link to="/" className="auth-logo">
            <div className="auth-logo-icon">
              <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
                <rect width="12" height="12" rx="3" fill="#444ce7" />
                <rect x="16" width="12" height="12" rx="3" fill="#f25c2b" />
                <rect y="16" width="12" height="12" rx="3" fill="#3b6df8" />
                <rect x="16" y="16" width="12" height="12" rx="3" fill="#a855f7" />
              </svg>
            </div>
            <div>
              <div className="auth-logo-brand">ROOMAN</div>
              <div className="auth-logo-product">Workspace</div>
            </div>
          </Link>
        </div>

        <div className="auth-left-content">
          <div className="auth-illustration">
            <div className="illus-project-card">
              <div className="illus-row">
                <span className="illus-dot teal" />
                <span className="illus-label">Task progress</span>
                <span className="illus-badge active">Tasks</span>
              </div>
              <div className="illus-progress">
                <div className="illus-progress-bar" style={{ width: '78%' }} />
              </div>
              <div className="illus-row illus-row-sm">
                <span className="illus-sub">Track progress across your projects</span>
              </div>
            </div>

            <div className="illus-project-card illus-card-offset">
              <div className="illus-row">
                <span className="illus-dot blue" />
                <span className="illus-label">Approvals queue</span>
                <span className="illus-badge active">Approvals</span>
              </div>
              <div className="illus-progress">
                <div className="illus-progress-bar blue" style={{ width: '64%' }} />
              </div>
              <div className="illus-row illus-row-sm">
                <span className="illus-sub">Review and approve logged time</span>
              </div>
            </div>

            <div className="illus-stat-pill">
              <span className="stat-icon"><Icon name="check" size={10} strokeWidth={3} /></span>
              <span>One workspace for your team</span>
            </div>
          </div>

          <h2 className="auth-left-title">Keep your team and work<br />in sync.</h2>
          <p className="auth-left-sub">
            Track tasks, approvals, timelines, and collaboration in one clean workspace.
          </p>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-form-container">
          <div className="auth-form-header">
            {mode === 'signin' ? (
              <>
                <h1 className="auth-form-title">Sign in</h1>
                <p className="auth-form-sub">
                  Don't have an account?{' '}
                  <Link to={registerHref} className="auth-link">Sign up free</Link>
                </p>
              </>
            ) : (
              <>
                <h1 className="auth-form-title">Reset your password</h1>
                <p className="auth-form-sub">
                  Enter your account email and we'll send you a link to set a new password.
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="auth-error" role="alert">
              <span className="auth-error-icon"><Icon name="warning" size={14} /></span>
              {error}
            </div>
          )}

          {notice && (
            <div className="auth-notice" role="status">
              <span className="auth-error-icon"><Icon name="checkCircle" size={14} /></span>
              {notice}
            </div>
          )}

          {mode === 'signin' ? (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="form-label" htmlFor="login-email-address">Email address</label>
              <input id="login-email-address"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="form-input"
                placeholder="you@company.com"
                autoFocus
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <div className="form-label-row">
                <label className="form-label" htmlFor="login-password">Password</label>
                <button type="button" className="form-forgot" onClick={() => { setError(''); setNotice(''); setMode('forgot'); }}>
                  Forgot password?
                </button>
              </div>
              <div className="form-input-wrapper">
                <input id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="form-eye"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
                </button>
              </div>
            </div>

            <button type="submit" className={`btn-submit ${loading ? 'btn-loading' : ''}`} disabled={loading}>
              {loading ? <span className="btn-spinner" /> : 'Sign in'}
            </button>
          </form>
          ) : (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <div className="form-group">
              <label className="form-label" htmlFor="forgot-email">Email address</label>
              <input id="forgot-email"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="form-input"
                placeholder="you@company.com"
                autoFocus
                autoComplete="email"
              />
            </div>
            <button type="submit" className={`btn-submit ${resetting ? 'btn-loading' : ''}`} disabled={resetting}>
              {resetting ? <span className="btn-spinner" /> : 'Send reset link'}
            </button>
            <button type="button" className="auth-back-link" onClick={() => { setError(''); setNotice(''); setMode('signin'); }}>
              ← Back to sign in
            </button>
          </form>
          )}

          {/*
            The Google and GitHub buttons are gone rather than disabled. There was no OAuth
            integration behind either of them, and what they actually did was fill in a
            real administrator credential. An affordance for a capability that does not
            exist is worth removing on its own; one that hands out an admin password is
            worth removing today.

            "Sign in with BOS" is the opposite case: it appears only when the server reports
            it is switched on and configured. Not with an invitation link either, whose
            acceptance belongs to the password sign-in above.
          */}
          {mode === 'signin' && bosEnabled && !inviteToken && (
            <>
              <div className="auth-divider"><span>or</span></div>
              <a
                className="social-btn auth-sso-btn"
                href={bosLoginUrl(returnPathAfterSignIn(location.state, location.search))}
              >
                Sign in with BOS
              </a>
            </>
          )}

          {mode === 'signin' && (
          <p className="auth-footer-note">
            By signing in, you agree to Rooman's{' '}
            <button type="button" className="auth-link" onClick={() => window.location.href = 'mailto:support@rooman.in?subject=Terms%20of%20Service'}>Terms of Service</button> and{' '}
            <button type="button" className="auth-link" onClick={() => window.location.href = 'mailto:support@rooman.in?subject=Privacy%20Policy'}>Privacy Policy</button>.
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
